import { useRef, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

export interface VirtualScrollOptions {
  count: number;
  estimatedRowHeight?: number;
  overscan?: number;
  onLoadMore?: () => void;
  isFetchingMore?: boolean;
}

export interface VirtualScrollState {
  scrollRef: React.RefObject<HTMLDivElement>;
  virtualItems: ReturnType<ReturnType<typeof useVirtualizer>["getVirtualItems"]>;
  paddingTop: number;
  paddingBottom: number;
  totalSize: number;
  measureElement: (element: Element | null) => void;
}

export function useVirtualScroll({
  count,
  estimatedRowHeight = 41,
  overscan = 5,
  onLoadMore,
  isFetchingMore,
}: VirtualScrollOptions): VirtualScrollState {
  const scrollRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan,
    measureElement: (el) => el?.getBoundingClientRect().height ?? estimatedRowHeight,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItem = virtualItems[virtualItems.length - 1];

  useEffect(() => {
    if (!onLoadMoreRef.current || isFetchingMore) return;
    if (lastVirtualItem && lastVirtualItem.index >= count - 1 - overscan) {
      onLoadMoreRef.current();
    }
  }, [lastVirtualItem?.index, count, overscan, isFetchingMore]);

  const paddingTop = virtualItems[0]?.start ?? 0;
  const paddingBottom =
    virtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0);

  return {
    scrollRef,
    virtualItems,
    paddingTop,
    paddingBottom,
    totalSize: virtualizer.getTotalSize(),
    measureElement: virtualizer.measureElement,
  };
}
